import { Compass, Hammer, Camera, MapPin, Calendar, Monitor, Moon, Feather, Wand2, Sparkles, Mountain, ScrollText } from "lucide-react";

const HERO = `${process.env.PUBLIC_URL}/wolf-grid-hero.webp`;

const WILL_LIVE_HERE = [
  { icon: Hammer, label: "Hollowmere build updates" },
  { icon: Compass, label: "Wolf Grid exploration" },
  { icon: Calendar, label: "Events and festivals" },
  { icon: Camera, label: "Screenshots and photography" },
  { icon: MapPin, label: "OpenSim travel" },
  { icon: Monitor, label: "Viewer features and discoveries" },
  { icon: Moon, label: "Seasonal changes" },
  { icon: Feather, label: "Roleplay development" },
  { icon: Wand2, label: "Strange experiments and things I accidentally build at 2 AM" },
];

export default function WolfGrid() {
  return (
    <div className="page-container fade-in" data-testid="wolf-grid-page">
      <div className="page-eyebrow">opensim · virtual worlds</div>
      <h1 className="page-title">Wolf Grid</h1>
      <p className="page-sub mt-3">
        Exploring Wolf Territories, building Hollowmere, and seeing where the road leads.
      </p>

      {/* HERO IMAGE — shown prominently, responsive, not aggressively cropped */}
      <figure className="parchment-card overflow-hidden p-2 md:p-3 mt-7" data-testid="wolf-hero">
        <img
          src={HERO}
          alt="A gathering of avatars dancing at an outdoor event in Wolf Territories Grid, with a fairground and stage in the background"
          className="w-full h-auto block rounded-sm"
          style={{ border: "1px solid rgba(212,175,55,0.22)" }}
        />
        <figcaption className="tavern-script text-center text-lg mt-3" style={{ color: "var(--orchid-light)" }}>
          a quiet evening on the grid — everyone gathered by the lights
        </figcaption>
      </figure>

      {/* INTRODUCTION */}
      <section className="mt-12">
        <h2 className="section-title flex items-center gap-2">
          <Compass className="h-5 w-5" style={{ color: "var(--gold)" }} /> A Grid Worth Wandering
        </h2>
        <div className="mt-5 space-y-4 max-w-[760px]" style={{ color: "var(--silver)", lineHeight: 1.85 }}>
          <p>
            Wolf Territories Grid is one of those places I originally expected to simply visit and
            somehow ended up genuinely enjoying.
          </p>
          <p>
            It is an OpenSim virtual world built around exploration, creativity, community, education,
            and giving people room to build things that feel personal.
          </p>
          <p>
            Membership is free, land is comparatively affordable, and the grid has continued developing
            its own tools, viewers, and features rather than simply copying what already exists elsewhere.
          </p>
          <p>One of the things that caught my attention first was the technology.</p>
          <p>
            Wolf Territories has both WolfViewer, a desktop viewer based on Firestorm, and WolfStorm,
            a browser-based viewer that allows people to enter the grid without installing a traditional viewer.
          </p>
          <p>
            The grid has also been experimenting with features such as terrain painting, region weather,
            water and wave controls, voice, screen sharing, increasingly large regions, and other tools
            designed specifically around the way people build and explore in OpenSim.
          </p>
          <p>What has kept me interested, though, is the community behind it.</p>
          <p>
            Wolf Territories places a strong emphasis on being welcoming to different kinds of people and
            different kinds of projects.
          </p>
          <p>
            There are educational spaces, research communities, neurodiversity-focused projects,
            autism-related regions and programs, and areas created by people who care about making virtual
            spaces accessible and welcoming to people who may not always feel represented elsewhere.
          </p>
          <p className="tavern-script text-2xl" style={{ color: "var(--orchid-light)" }}>
            That matters to me.
          </p>
          <p>I think the grid has enormous potential if more people discover it.</p>
        </div>
      </section>

      {/* HOLLOWMERE */}
      <section className="mt-14">
        <div className="parchment-card p-7 md:p-10" data-testid="hollowmere-section">
          <div className="page-eyebrow">a work in progress</div>
          <h2 className="tavern-heading text-3xl md:text-4xl gold-text mt-1 flex items-center gap-3">
            <Mountain className="h-7 w-7" style={{ color: "var(--gold)" }} /> Hollowmere
          </h2>
          <div className="mt-5 space-y-4 max-w-[760px]" style={{ color: "var(--silver)", lineHeight: 1.85 }}>
            <p>Hollowmere is currently a work in progress.</p>
            <p>
              What started as an idea for a fantasy roleplay region has slowly grown into something a little broader.
            </p>
            <p>
              I am experimenting with the idea of a creative, seasonal world that changes over time — a place people
              can explore, photograph, wander through, attend events in, and possibly roleplay in as the community develops.
            </p>
            <p>I do not want Hollowmere to exist only for roleplay.</p>
            <p>
              I want it to feel like a place worth visiting even if someone simply wants to explore, take photographs,
              attend an event, discover a strange little corner, or sit somewhere quiet for a while.
            </p>
            <p>
              At the moment, I am still building, landscaping, experimenting with Wolf's tools, and figuring out what
              Hollowmere wants to become.
            </p>
            <p className="tavern-script text-2xl" style={{ color: "var(--orchid-light)" }}>That is part of the fun.</p>
          </div>
        </div>
      </section>

      {/* WHAT WILL LIVE HERE */}
      <section className="mt-14">
        <h2 className="section-title flex items-center gap-2">
          <Sparkles className="h-5 w-5" style={{ color: "var(--gold)" }} /> What Will Live Here
        </h2>
        <p className="mt-3 max-w-[760px]" style={{ color: "var(--muted)" }}>
          Over time, this page will gather the bits and pieces of the journey:
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {WILL_LIVE_HERE.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="room-card p-5 flex items-start gap-3" data-testid="wolf-expect-card">
                <div className="h-9 w-9 grid place-items-center rounded-sm flex-shrink-0"
                  style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(212,175,55,0.25)" }}>
                  <Icon className="h-4 w-4" style={{ color: "var(--gold)" }} />
                </div>
                <span className="tavern-heading text-lg" style={{ color: "var(--silver)", lineHeight: 1.3 }}>
                  {item.label}
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-7 space-y-4 max-w-[760px]" style={{ color: "var(--silver)", lineHeight: 1.85 }}>
          <p>
            This section of the Tavern will follow that process: builds, screenshots, events, discoveries,
            strange experiments, Wolf Grid travel, and whatever else I stumble into along the way.
          </p>
          <p>
            If the project helps even a few more people discover Wolf Territories, wander around, and see what
            people are creating there, I will consider that a win.
          </p>
        </div>
      </section>

      {/* FROM THE GRID (future posts/updates) */}
      <section className="mt-14">
        <h2 className="section-title flex items-center gap-2">
          <ScrollText className="h-5 w-5" style={{ color: "var(--gold)" }} /> From the Grid
        </h2>
        <div className="parchment-card p-6 md:p-8 mt-5 text-center" data-testid="from-the-grid-empty">
          <Feather className="h-7 w-7 mx-auto" style={{ color: "rgba(212,175,55,0.6)" }} />
          <p className="tavern-script text-2xl mt-3" style={{ color: "var(--orchid-light)" }}>
            The fire is warm and the quill is ready.
          </p>
          <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
            Stories, builds, events, and wandering notes from Wolf Territories will appear here.
          </p>
        </div>
      </section>
    </div>
  );
}
